/**
 * Chat Actions
 *
 * Action creators and handlers for chat operations
 */

import { Conversation, ConversationFilters } from "@/types/centralizedChat";
import { ChatImageUploadResult } from "@/utils/chatImageUtils";
import { logger } from "@/utils/logger";
import { fetchWithAuthRetry } from "@/utils/auth-helpers";
import {
  fetchConversations,
  createOrGetConversation,
} from "./services/conversationService";
import {
  fetchMessages,
  sendMessage as sendMessageService,
} from "./services/messageService";
import {
  fetchUnreadCount,
  markConversationAsRead,
  markAllConversationsAsRead,
} from "./services/notificationService";
import { messageCache } from "./cache/messageCache";
import { conversationCache } from "./cache/conversationCache";
import { MessageServiceContext } from "./services/types";
import { ChatActions } from "./types";

export interface ChatActionsConfig {
  userId: string | null;
  user: { id: string; full_name?: string | null } | null;
  roles: { admin?: boolean; manufacturer?: boolean } | null;
  setState: (updater: (state: any) => any) => void;
  getState: () => any;
}

export function createChatActions(config: ChatActionsConfig) {
  const { userId, user, roles, setState, getState } = config;

  const getMessageContext = (
    conversation?: Conversation | null
  ): MessageServiceContext => ({
    user: user || { id: "", full_name: null },
    roles,
    conversation: conversation || getState().selectedConversation,
  });

  // Create actions object that can reference itself
  const actions: any = {};

  // Define loadMessages first so it can be referenced
  const loadMessages = async (conversationId: string, page: number = 1) => {
    if (!conversationId || !user) return;

    setState((state: any) => ({
      ...state,
      isLoadingMessages: page === 1,
      isLoadingMoreMessages: page > 1,
      messagesError: null,
    }));

    try {
      // Check cache
      const cached = messageCache.get(conversationId, page);
      if (cached) {
        setState((state: any) => ({
          ...state,
          messages:
            page === 1 ? cached.data : [...cached.data, ...state.messages],
          messagePagination: cached.pagination,
          isLoadingMessages: false,
          isLoadingMoreMessages: false,
        }));
        return;
      }

      const context = getMessageContext();
      const result = await fetchMessages(conversationId, page, context);

      // Update cache
      messageCache.set(
        conversationId,
        page,
        result.messages,
        result.pagination
      );

      setState((state: any) => ({
        ...state,
        messages:
          page === 1
            ? result.messages
            : [...result.messages, ...state.messages],
        messagePagination: result.pagination,
        isLoadingMessages: false,
        isLoadingMoreMessages: false,
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unable to load messages";
      setState((state: any) => ({
        ...state,
        isLoadingMessages: false,
        isLoadingMoreMessages: false,
        messagesError: errorMessage,
      }));
    }
  };

  // Conversation actions
  actions.selectConversation = async (conversation: Conversation | null) => {
    setState((state: any) => ({
      ...state,
      selectedConversation: conversation,
      messages: [],
      messagePagination: null,
      messagesError: null,
    }));

    if (conversation && userId) {
      // Mark as read
      try {
        await markConversationAsRead(userId, conversation.conversationId);

        // Update unread count
        setState((state: any) => {
          const newByConversation = { ...state.unreadCount.byConversation };
          const removedCount =
            newByConversation[conversation.conversationId] || 0;
          delete newByConversation[conversation.conversationId];
          return {
            ...state,
            unreadCount: {
              total: state.unreadCount.total - removedCount,
              byConversation: newByConversation,
            },
          };
        });
      } catch {
        logger.error("Error marking conversation as read", {
          component: "chatActions",
          conversationId: conversation.conversationId,
        });
      }

      // Fetch messages
      await loadMessages(conversation.conversationId, 1);
    }
  };

  actions.fetchConversations = async (filters: ConversationFilters = {}) => {
    if (!userId) return;

    setState((state: any) => ({
      ...state,
      isLoadingConversations: true,
      conversationsError: null,
    }));

    try {
      // Check cache first
      const cached = conversationCache.get(filters);
      if (cached && cached.timestamp > Date.now() - 30000) {
        setState((state: any) => ({
          ...state,
          conversations: cached.data,
          isLoadingConversations: false,
          lastFetchTime: cached.timestamp,
        }));
        return;
      }

      const result = await fetchConversations(filters);

      // Update cache
      conversationCache.set(result.conversations, filters);

      setState((state: any) => ({
        ...state,
        conversations: result.conversations,
        isLoadingConversations: false,
        lastFetchTime: result.timestamp,
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unable to load conversations";
      setState((state: any) => ({
        ...state,
        isLoadingConversations: false,
        conversationsError: errorMessage,
      }));
    }
  };

  actions.refetchConversations = async () => {
    const state = getState();
    conversationCache.invalidate(state.conversationFilters);
    await actions.fetchConversations(state.conversationFilters);
  };

  actions.updateFilters = (filters: ConversationFilters) => {
    setState((state: any) => ({
      ...state,
      conversationFilters: filters,
    }));
  };

  // Message actions
  actions.loadMessages = loadMessages;

  actions.loadMoreMessages = async () => {
    const state = getState();
    if (!state.selectedConversation || !state.messagePagination?.hasMore)
      return;

    const nextPage = state.messagePagination.currentPage + 1;
    await loadMessages(state.selectedConversation.conversationId, nextPage);
  };

  actions.sendMessage = async (
    conversationId: string,
    content: string,
    options?: { imagePath?: string | null; referencedTicketId?: string | null }
  ): Promise<void> => {
    if (!user || !conversationId) return;

    const trimmedContent = content.trim();
    const imagePath = options?.imagePath || null;
    const referencedTicketId = options?.referencedTicketId || null;

    if (!trimmedContent && !imagePath) return;

    setState((state: any) => ({
      ...state,
      isSendingMessage: true,
    }));

    try {
      const context = getMessageContext();
      const newMessage = await sendMessageService(
        conversationId,
        trimmedContent,
        context,
        { imagePath, referencedTicketId }
      );

      // Add message to state
      setState((state: any) => ({
        ...state,
        messages: [...state.messages, newMessage],
      }));

      // Invalidate cache
      messageCache.invalidate(conversationId);

      // Refresh conversations list to ensure new conversations appear in sidebar
      // This is especially important for new conversations that only show up after first message
      await actions.refetchConversations();
    } catch (error) {
      logger.error("Error sending message", {
        component: "chatActions",
        conversationId,
      });
      throw error;
    } finally {
      setState((state: any) => ({
        ...state,
        isSendingMessage: false,
      }));
    }
  };

  actions.uploadImage = async (
    conversationId: string,
    file: File
  ): Promise<ChatImageUploadResult> => {
    if (!user) {
      throw new Error("Authentication required");
    }

    const formData = new FormData();
    formData.append("conversationId", conversationId);
    formData.append("file", file);

    const response = await fetch("/api/messages/upload-image", {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => null);
      const errorMessage =
        errorPayload?.error || "Unable to upload image. Please try again.";
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result.data as ChatImageUploadResult;
  };

  // Conversation creation
  actions.startConversationWithManufacturer = async (
    manufacturerCompanyId: string
  ) => {
    if (!user || !userId) return;

    try {
      const conversationId = await createOrGetConversation(
        manufacturerCompanyId
      );

      // Fetch conversation details via API route
      const convResponse = await fetch(
        `/api/conversations?conversationId=${conversationId}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!convResponse.ok) {
        throw new Error("Failed to fetch conversation details");
      }

      const convData = await convResponse.json();
      const conversations = convData.data?.conversations || [];
      const convResult = conversations.find(
        (c: any) => c.conversationId === conversationId
      );

      if (!convResult) {
        throw new Error("Conversation not found");
      }

      // Use the conversation from API response
      const conversation: Conversation = convResult;

      await actions.selectConversation(conversation);
      await actions.refetchConversations();
    } catch (error) {
      logger.error("Error starting conversation with manufacturer", {
        component: "chatActions",
        manufacturerCompanyId,
      });
      throw error;
    }
  };

  actions.startConversationFromTicket = async (ticketId: string) => {
    if (!user || !userId) return;

    try {
      // Fetch ticket data
      const response = await fetchWithAuthRetry(
        `/api/tickets/available-for-chat?page=0&limit=1000`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch ticket data");
      }

      const data = await response.json();
      const ticket = data.data?.tickets?.find((t: any) => t.id === ticketId);

      if (!ticket || !ticket.approved_company) {
        throw new Error("Ticket not found or has no approved manufacturer");
      }

      const conversationId = await createOrGetConversation(
        ticket.approved_company
      );

      // Build conversation object
      const conversation: Conversation = {
        conversationId,
        manufacturerCompanyId: ticket.approved_company,
        ticketId: ticket.id,
        ticketTitle: ticket.prompt
          ? `${ticket.prompt.slice(0, 50)}${ticket.prompt.length > 50 ? "..." : ""}`
          : `Ticket #${ticket.id.slice(0, 8)}`,
        ticketStatus: ticket.status || "pending",
        lastMessage: null,
        unreadCount: 0,
        lastActivity: ticket.created_at || new Date().toISOString(),
        participants: [],
      };

      await actions.selectConversation(conversation);
    } catch (error) {
      logger.error("Error starting conversation from ticket", {
        component: "chatActions",
        ticketId,
      });
      throw error;
    }
  };

  // Notification actions
  actions.markAsRead = async (conversationId: string) => {
    if (!userId) return;
    await markConversationAsRead(userId, conversationId);
  };

  actions.markAllAsRead = async () => {
    if (!userId) return;
    const state = getState();
    const conversationIds = Object.keys(state.unreadCount.byConversation);
    if (conversationIds.length === 0) return;
    await markAllConversationsAsRead(userId, conversationIds);

    setState((state: any) => ({
      ...state,
      unreadCount: { total: 0, byConversation: {} },
      notifications: [],
    }));
  };

  actions.refreshNotifications = async () => {
    if (!userId) return;

    try {
      const unreadCount = await fetchUnreadCount(userId);
      setState((state: any) => ({
        ...state,
        unreadCount,
      }));
    } catch {
      logger.error("Error refreshing notifications", {
        component: "chatActions",
      });
    }
  };

  return actions as ChatActions;
}
